/**
 * Progress, streak and metering components.
 *
 * Two product rules are encoded here rather than left to each screen:
 *   - the usage meter is always visible and always honest (never hidden, never vague)
 *   - a `LimitSheet` cannot be constructed without a free alternative, because the type
 *     from `@saysooner/core` requires a non-empty list
 */

import type { FreeAlternative, MeteredLimit, Pillar } from '@saysooner/core';
import type { JSX } from 'react';

export interface GoalRingProps {
  readonly minutes: number;
  readonly activeSeconds: number;
  readonly met: boolean;
}

/** Minutes, not lesson counts — the honest unit for a speaking product. */
export function GoalRing({ minutes, activeSeconds, met }: GoalRingProps): JSX.Element {
  const doneMinutes = Math.floor(activeSeconds / 60);
  const fraction = Math.min(1, activeSeconds / Math.max(1, minutes * 60));
  const remaining = Math.max(0, minutes - doneMinutes);
  const size = 104;
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      className="vl-goal"
      role="img"
      aria-label={
        met
          ? `Today's goal met: ${doneMinutes} of ${minutes} minutes.`
          : `${doneMinutes} of ${minutes} minutes done. ${remaining} to go.`
      }
      data-met={met ? 'true' : 'false'}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="vl-goal__track"
          fill="none"
          strokeWidth={10}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="vl-goal__value"
          fill="none"
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="vl-goal__content" aria-hidden="true">
        <span className="vl-goal__numeral">
          {doneMinutes}/{minutes}
        </span>
        <span className="vl-goal__unit">min</span>
      </div>
    </div>
  );
}

export type DayMark = 'done' | 'missed' | 'frozen' | 'today' | 'future';

export interface StreakStripProps {
  readonly strip: readonly { localDate: string; mark: DayMark }[];
  readonly currentDays: number;
  readonly currentWeeks: number;
  readonly freezesLeft: number;
  readonly onOpen?: (() => void) | undefined;
}

const DAY_GLYPH: Record<DayMark, string> = {
  done: '✓',
  missed: '·',
  frozen: '❄',
  today: '○',
  future: '',
};

const DAY_WORD: Record<DayMark, string> = {
  done: 'goal met',
  missed: 'missed',
  frozen: 'freeze used',
  today: 'today, not yet met',
  future: 'upcoming',
};

export function StreakStrip({
  strip,
  currentDays,
  currentWeeks,
  freezesLeft,
  onOpen,
}: StreakStripProps): JSX.Element {
  const content = (
    <>
      <ol className="vl-streak__days">
        {strip.map((day) => (
          <li
            key={day.localDate}
            className="vl-streak__day"
            data-mark={day.mark}
            aria-label={`${day.localDate}: ${DAY_WORD[day.mark]}`}
          >
            <span aria-hidden="true">{DAY_GLYPH[day.mark]}</span>
          </li>
        ))}
      </ol>
      <p className="vl-streak__summary">
        <span className="vl-streak__flame" aria-hidden="true">
          🔥
        </span>
        {currentDays} {currentDays === 1 ? 'day' : 'days'}
        {/* The forgiving second axis, so one missed day does not zero the identity. */}
        {currentWeeks > 0 && (
          <span className="vl-streak__weeks">
            {' '}
            · {currentWeeks} {currentWeeks === 1 ? 'week' : 'weeks'} in a row
          </span>
        )}
        {freezesLeft > 0 && (
          <span className="vl-streak__freezes">
            {' '}
            · {freezesLeft} {freezesLeft === 1 ? 'freeze' : 'freezes'} left
          </span>
        )}
      </p>
    </>
  );

  return onOpen === undefined ? (
    <section className="vl-streak" aria-label="Your streak">
      {content}
    </section>
  ) : (
    <button type="button" className="vl-streak vl-streak--button" onClick={onOpen}>
      {content}
    </button>
  );
}

export interface UsageMeterProps {
  readonly voiceSecondsUsed: number;
  /** `null` means unmetered. */
  readonly voiceSecondsTotal: number | null;
  readonly state: 'unlimited' | 'ample' | 'low' | 'exhausted';
  readonly resetsAt: number;
  readonly onOpen?: (() => void) | undefined;
}

/**
 * Always visible, always exact. Jumpspeak's honest "3/3 conversations left" is the
 * pattern; vagueness here is what makes a limit feel like a trap.
 */
export function UsageMeter({
  voiceSecondsUsed,
  voiceSecondsTotal,
  state,
  resetsAt,
  onOpen,
}: UsageMeterProps): JSX.Element {
  if (state === 'unlimited' || voiceSecondsTotal === null) {
    return (
      <span className="vl-meter" data-state="unlimited">
        <span aria-hidden="true">🎙</span> Unlimited
      </span>
    );
  }

  const usedMinutes = Math.floor(voiceSecondsUsed / 60);
  const totalMinutes = Math.round(voiceSecondsTotal / 60);
  const label = `${usedMinutes} of ${totalMinutes} voice minutes used today. Resets ${formatReset(resetsAt)}.`;

  const inner = (
    <>
      <span aria-hidden="true">🎙</span>
      <span aria-hidden="true">
        {usedMinutes}/{totalMinutes} min
      </span>
      <span className="vl-visually-hidden">{label}</span>
    </>
  );

  return onOpen === undefined ? (
    <span className="vl-meter" data-state={state} title={label}>
      {inner}
    </span>
  ) : (
    <button type="button" className="vl-meter vl-meter--button" data-state={state} onClick={onOpen}>
      {inner}
    </button>
  );
}

function formatReset(resetsAt: number): string {
  const remaining = resetsAt - Date.now();
  if (remaining <= 0) return 'shortly';
  const hours = Math.floor(remaining / 3_600_000);
  const minutes = Math.round((remaining % 3_600_000) / 60_000);
  if (hours === 0) return `in ${minutes}m`;
  return `in ${hours}h ${minutes}m`;
}

export interface PillarCardProps {
  readonly pillar: Pillar;
  readonly score: number;
  readonly delta7d: number;
  readonly evaluatedAt: number;
  readonly progressLabel?: string | undefined;
  readonly needsWork: boolean;
  readonly onOpen: () => void;
}

const PILLAR_LABEL: Record<Pillar, string> = {
  speak: 'Speak',
  words: 'Words',
  grammar: 'Grammar',
  listen: 'Listen',
  confidence: 'Confidence',
};

const PILLAR_BLURB: Record<Pillar, string> = {
  speak: 'Practise real conversations',
  words: 'Learn and recall words',
  grammar: 'Rules and writing',
  listen: 'Train your ear',
  confidence: 'Sound sure of yourself',
};

export function PillarCard({
  pillar,
  score,
  delta7d,
  evaluatedAt,
  progressLabel,
  needsWork,
  onOpen,
}: PillarCardProps): JSX.Element {
  return (
    <button
      type="button"
      className="vl-pillar"
      data-pillar={pillar}
      data-needs-work={needsWork ? 'true' : 'false'}
      onClick={onOpen}
      aria-label={`${PILLAR_LABEL[pillar]}: ${Math.round(score)} out of 100${
        delta7d === 0
          ? ''
          : `, ${delta7d > 0 ? 'up' : 'down'} ${Math.abs(Math.round(delta7d))} this week`
      }${needsWork ? '. Needs work.' : ''}`}
    >
      <span className="vl-pillar__name">{PILLAR_LABEL[pillar]}</span>
      <span className="vl-pillar__blurb">{PILLAR_BLURB[pillar]}</span>
      <span className="vl-pillar__score" aria-hidden="true">
        {Math.round(score)}
        {delta7d !== 0 && (
          <span className="vl-pillar__delta" data-direction={delta7d > 0 ? 'up' : 'down'}>
            {delta7d > 0 ? '▲' : '▼'}
            {Math.abs(Math.round(delta7d))}
          </span>
        )}
      </span>
      {progressLabel !== undefined && <span className="vl-pillar__progress">{progressLabel}</span>}
      {/* Staleness is disclosed rather than hidden. */}
      <span className="vl-pillar__evaluated">Last checked {formatDate(evaluatedAt)}</span>
      {needsWork && <span className="vl-pillar__flag">Needs work</span>}
    </button>
  );
}

function formatDate(ms: number): string {
  if (ms === 0) return 'never';
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(ms).toISOString().slice(0, 10);
}

export interface NextActionCardProps {
  readonly title: string;
  readonly goal: string | null;
  readonly reason: string;
  readonly estimatedMinutes: number;
  readonly requiresVoice: boolean;
  readonly onStart: () => void;
  /** Re-rolls the recommendation; the rejection is logged as a signal. */
  readonly onReject?: (() => void) | undefined;
}

/** Exactly one recommendation, with its reason stated. */
export function NextActionCard({
  title,
  goal,
  reason,
  estimatedMinutes,
  requiresVoice,
  onStart,
  onReject,
}: NextActionCardProps): JSX.Element {
  return (
    <section className="vl-next" aria-labelledby="vl-next-title">
      <p className="vl-next__eyebrow">Next up</p>
      <h2 className="vl-next__title" id="vl-next-title">
        {title}
      </h2>
      {goal !== null && <p className="vl-next__goal">Goal: {goal}</p>}
      <p className="vl-next__meta">
        {reason} · about {estimatedMinutes} min
        {requiresVoice && (
          <>
            {' '}
            · <span aria-hidden="true">🎙 voice</span>
            <span className="vl-visually-hidden">uses voice</span>
          </>
        )}
      </p>
      <div className="vl-next__actions">
        <button type="button" className="vl-button vl-button--primary" onClick={onStart}>
          Continue
        </button>
        {onReject !== undefined && (
          <button
            type="button"
            className="vl-button vl-button--ghost vl-button--sm"
            onClick={onReject}
          >
            Not this
          </button>
        )}
      </div>
    </section>
  );
}

export interface FocusListProps {
  readonly items: readonly {
    readonly kind: string;
    readonly title: string;
    readonly detail: string;
    readonly estimatedMinutes: number;
    readonly route: string;
  }[];
  readonly onOpen: (route: string) => void;
}

/** At most three bounded tasks. The cap is enforced upstream and relied on here. */
export function FocusList({ items, onOpen }: FocusListProps): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section className="vl-focus" aria-label="Focus this week">
      <h3 className="vl-focus__title">Focus this week</h3>
      <ul className="vl-focus__list">
        {items.slice(0, 3).map((item) => (
          <li key={`${item.kind}-${item.title}`}>
            <button type="button" className="vl-focus__item" onClick={() => onOpen(item.route)}>
              <span className="vl-focus__item-title">{item.title}</span>
              <span className="vl-focus__item-detail">{item.detail}</span>
              <span className="vl-focus__item-time">{item.estimatedMinutes} min</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface LimitSheetProps {
  readonly limit: MeteredLimit;
  readonly resetsAt: number;
  /** Non-empty by type. A limit without a free alternative cannot be rendered. */
  readonly alternatives: readonly [FreeAlternative, ...FreeAlternative[]];
  readonly upgradeHeadline: string;
  readonly onAlternative: (route: string) => void;
  readonly onUpgrade: () => void;
  readonly onDismiss: () => void;
}

/**
 * The graceful limit sheet. The free alternatives come first and the upgrade sits below
 * the information, not above it (docs/04-screen-specs.md G6).
 */
export function LimitSheet({
  limit,
  resetsAt,
  alternatives,
  upgradeHeadline,
  onAlternative,
  onUpgrade,
  onDismiss,
}: LimitSheetProps): JSX.Element {
  const title =
    limit === 'voice_minutes'
      ? "You've used today's voice minutes."
      : "You've used today's custom scenario.";

  return (
    <div className="vl-sheet" role="dialog" aria-modal="true" aria-labelledby="vl-limit-title">
      <h2 className="vl-sheet__title" id="vl-limit-title">
        {title}
      </h2>
      <p className="vl-sheet__meta">Resets {formatReset(resetsAt)}</p>

      <h3 className="vl-sheet__subtitle">You can still, free:</h3>
      <ul className="vl-sheet__alternatives">
        {alternatives.map((alternative) => (
          <li key={alternative.kind}>
            <button
              type="button"
              className="vl-sheet__alternative"
              onClick={() => onAlternative(alternative.route)}
            >
              <span>{alternative.label}</span>
              <span className="vl-sheet__alternative-time">{alternative.estimatedMinutes} min</span>
            </button>
          </li>
        ))}
      </ul>

      <button type="button" className="vl-button vl-button--ghost" onClick={onUpgrade}>
        {upgradeHeadline}
      </button>
      <button type="button" className="vl-button vl-button--secondary" onClick={onDismiss}>
        Not now
      </button>
    </div>
  );
}

export interface CefrBadgeProps {
  readonly level: string;
  readonly bandLabel: string;
  /**
   * How sure we are of the *learner's* level, 0–1, with the width of the band.
   *
   * Both optional together, and omitted means no claim is shown. The learn path used to
   * pass `confidence={1}` and `bandSublevels={0}` because the props were required, so
   * every module on a brand-new course announced "confident · 100% confident" — a
   * statement about the learner, invented, next to a label that is really about the
   * content. A module's CEFR level is a fact about the lesson; the error bars belong to
   * the estimate of the person.
   */
  readonly confidence?: number | undefined;
  readonly bandSublevels?: number | undefined;
  readonly onOpen?: (() => void) | undefined;
}

/**
 * The level, shown with its error bars. Every competitor shows a vanity percentage; the
 * confidence band is what makes ours defensible — which is exactly why it must not be
 * printed where there is no estimate behind it.
 */
export function CefrBadge({
  level,
  bandLabel,
  confidence,
  bandSublevels,
  onOpen,
}: CefrBadgeProps): JSX.Element {
  const estimated = confidence !== undefined && bandSublevels !== undefined;
  const uncertainty =
    bandSublevels === 0
      ? 'confident'
      : `±${bandSublevels} sub-level${bandSublevels === 1 ? '' : 's'}`;

  const inner = (
    <>
      <span className="vl-cefr__level">{level}</span>
      <span className="vl-cefr__band">{bandLabel}</span>
      {estimated && (
        <span className="vl-cefr__confidence">
          {uncertainty} · {Math.round(confidence * 100)}% confident
        </span>
      )}
    </>
  );

  // The accessible name has to drop the band too. Built from `uncertainty` regardless,
  // it read "Level A1.1, A1, ±undefined sub-levels" the moment the estimate was absent —
  // clean on screen and broken for a screen reader, which is the half nobody looks at.
  const name = estimated
    ? `Level ${level}, ${bandLabel}, ${uncertainty}`
    : `Level ${level}, ${bandLabel}`;

  return onOpen === undefined ? (
    <section className="vl-cefr" aria-label={name}>
      {inner}
    </section>
  ) : (
    <button
      type="button"
      className="vl-cefr vl-cefr--button"
      onClick={onOpen}
      aria-label={`${name}. See how this was worked out.`}
    >
      {inner}
    </button>
  );
}

export interface SparklineProps {
  readonly values: readonly number[];
  /** What the series is, in words. The chart is nothing at all without it. */
  readonly label: string;
  readonly height?: number;
}

/**
 * A trend line over a short series.
 *
 * `04-screen-specs` asks for one in two places — E4's per-sound progress and F1's level
 * history — and there was no chart primitive on any platform. The review forecast E1
 * calls a chart is a labelled list of counts, which is right for seven labelled values
 * and wrong for twenty unlabelled ones.
 *
 * The line is never the only representation: the `svg` is `aria-hidden` and the wrapper
 * carries the sentence, the same arrangement `GoalRing` uses above.
 */
export function Sparkline({ values, label, height = 40 }: SparklineProps): JSX.Element | null {
  if (values.length === 0) return null;

  const width = 160;
  const inset = 2;
  // The vertical scale, widened when the series is flat. Identical scores are a real
  // thing a learner can have — it is what "no progress yet" looks like — and scaling by
  // the span would divide by zero on exactly that series.
  const low = Math.min(...values);
  const high = Math.max(...values);
  const flat = high - low < 1;
  const base = flat ? low - 1 : low;
  const span = flat ? 2 : high - low;

  const at = (value: number, index: number): readonly [number, number] => [
    values.length === 1 ? width / 2 : inset + ((width - inset * 2) * index) / (values.length - 1),
    inset + (height - inset * 2) * (1 - (value - base) / span),
  ];

  return (
    <div className="vl-sparkline" role="img" aria-label={label}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
        focusable="false"
      >
        {values.length === 1 ? (
          // A single attempt is a dot, not a line: one point drawn as a polyline is
          // invisible, which reads as "no data" when there is some.
          <circle
            className="vl-sparkline__point"
            cx={at(values[0] as number, 0)[0]}
            cy={at(values[0] as number, 0)[1]}
            r={3}
          />
        ) : (
          <polyline
            className="vl-sparkline__line"
            fill="none"
            points={values.map((value, index) => at(value, index).join(',')).join(' ')}
          />
        )}
      </svg>
    </div>
  );
}
